/**
 * P4-07: CalendarBlock —— 右栏顶部日历块。
 *
 * 取代 `js/ui.js:722-756` 的 `updateCalendar`：
 * - 日历科技研究后显示 `year / season / day`；
 * - 天文学研究后显示星座符号并着色；
 * - 写入 `document.title`；
 * - tooltip 通过 `TooltipAnchor` 实现（calendarDiv 显示年份/真实年份信息，
 *   calendarSign 显示 cycle 效果）。
 */
import { useCallback, useEffect } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";
import { TooltipAnchor } from "../../components/Tooltip/TooltipProvider";

interface CalendarSnapshot {
  hasCalendarTech: boolean;
  hasAstronomyTech: boolean;
  year: number;
  seasonTitle: string;
  day: number;
  weather: string | null;
  cycle: number;
  cycleYear: number;
  cycleGlyph: string;
  cycleColor: string;
  cycleTitle: string;
  ironWill: boolean;
  observeBtn: boolean;
  hasParadoxalKnowledge: boolean;
  hasNumerology: boolean;
  hasNumeromancy: boolean;
  festivalDays: number;
  trueYear: number;
}

function getCalendarSnapshot(game: GamePage | null): CalendarSnapshot | null {
  if (!game?.calendar) {
    return null;
  }
  const calendar = game.calendar;
  const hasCalendarTech = game.science?.get("calendar")?.researched ?? false;
  const hasAstronomyTech = game.science?.get("astronomy")?.researched ?? false;
  const cycle = calendar.cycles?.[calendar.cycle];

  return {
    hasCalendarTech,
    hasAstronomyTech,
    year: calendar.year,
    seasonTitle: calendar.getCurSeasonTitle?.() ?? "",
    day: Math.floor(calendar.day),
    weather: calendar.weather ?? null,
    cycle: calendar.cycle,
    cycleYear: calendar.cycleYear,
    cycleGlyph: cycle?.glyph ?? "",
    cycleColor: calendar.cycleYearColor?.() ?? "",
    cycleTitle: cycle?.title ?? "",
    ironWill: game.ironWill,
    observeBtn: calendar.observeBtn,
    hasParadoxalKnowledge: game.science?.get("paradoxalKnowledge")?.researched ?? false,
    hasNumerology: game.prestige?.getPerk("numerology")?.researched ?? false,
    hasNumeromancy: game.prestige?.getPerk("numeromancy")?.researched ?? false,
    festivalDays: calendar.festivalDays ?? 0,
    trueYear: Math.trunc(calendar.trueYear?.() ?? calendar.year),
  };
}

function snapEq(a: CalendarSnapshot | null, b: CalendarSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.hasCalendarTech === b.hasCalendarTech &&
    a.hasAstronomyTech === b.hasAstronomyTech &&
    a.year === b.year &&
    a.seasonTitle === b.seasonTitle &&
    a.day === b.day &&
    a.weather === b.weather &&
    a.cycle === b.cycle &&
    a.cycleYear === b.cycleYear &&
    a.cycleColor === b.cycleColor &&
    a.ironWill === b.ironWill &&
    a.observeBtn === b.observeBtn &&
    a.hasParadoxalKnowledge === b.hasParadoxalKnowledge &&
    a.hasNumerology === b.hasNumerology &&
    a.hasNumeromancy === b.hasNumeromancy &&
    a.festivalDays === b.festivalDays &&
    a.trueYear === b.trueYear
  );
}

function formatYear(year: number): string {
  return year > 100000 ? year.toLocaleString() : String(year);
}

export default function CalendarBlock() {
  const selector = useCallback(
    (game: GamePage | null) => getCalendarSnapshot(game),
    [],
  );
  const snapshot = useGameTick(selector, snapEq);

  // document.title 副作用：与旧 updateCalendar 一致。
  useEffect(() => {
    if (!snapshot?.hasCalendarTech) {
      return;
    }
    const yearText = formatYear(snapshot.year);
    const title = `${t("navbar.title")} - ${t("calendar.year.full", [yearText, snapshot.seasonTitle, snapshot.day])}`;
    document.title = snapshot.ironWill && snapshot.observeBtn ? `[EVENT!]${title}` : title;
  }, [snapshot]);

  if (!snapshot) {
    return (
      <div>
        <span id="calendarSign" />
        <span id="calendarDiv">Calendar :V</span>
      </div>
    );
  }

  const getCalendarTooltip = () => {
    const parts: string[] = [];
    const displayThreshold = 100000;
    if (snapshot.year > displayThreshold) {
      parts.push(`${t("calendar.year.tooltip")} ${snapshot.year.toLocaleString()}`);
    }
    if (snapshot.hasParadoxalKnowledge) {
      const trueYearText = snapshot.trueYear > displayThreshold ? snapshot.trueYear.toLocaleString() : String(snapshot.trueYear);
      parts.push(`${t("calendar.trueYear")} ${trueYearText}`);
    }
    return parts.join("<br>");
  };

  const getSignTooltip = () => {
    if (!snapshot.cycleTitle) {
      return "";
    }
    const cycleHeader = `${snapshot.cycleTitle} (${t("calendar.year")} ${snapshot.cycleYear})`;
    const lines = [
      `<div style="text-align:center;clear:both;">${cycleHeader}</div>`,
    ];

    // TODO: 完整复刻 numerology / numeromancy cycle effects 需要 cycle.effects 与
    // game.getEffectMeta。当前先保留 cycle 标题 tooltip；后续可扩展为完整效果表。
    return lines.join("");
  };

  const weatherSuffix = snapshot.weather
    ? ` (${t("calendar.weather." + snapshot.weather)})`
    : "";
  const calendarText = snapshot.hasCalendarTech
    ? t("calendar.year.full", [
        formatYear(snapshot.year),
        snapshot.seasonTitle + weatherSuffix,
        snapshot.day,
      ])
    : snapshot.seasonTitle;

  return (
    <div>
      {snapshot.hasAstronomyTech && snapshot.cycleGlyph && (
        <span id="calendarSign">
          <TooltipAnchor
            className="calendar-sign"
            style={{ color: snapshot.cycleColor, cursor: "pointer" }}
            getTooltip={getSignTooltip}
          >
            {snapshot.cycleGlyph}{" "}
          </TooltipAnchor>
        </span>
      )}
      <span id="calendarDiv">
        <TooltipAnchor
          className="calendar-div"
          style={{ cursor: "pointer" }}
          getTooltip={getCalendarTooltip}
        >
          {calendarText}
        </TooltipAnchor>
      </span>
    </div>
  );
}
